/**
 * Service Configuration - Single Source of Truth
 *
 * ALL service URLs are resolved here. No other file should define service URLs.
 *
 * How it works:
 * - In DEVELOPMENT: Returns relative paths (e.g., '/transport-service')
 *   which are proxied by Vite to the correct localhost port.
 * - In PRODUCTION/QA: Returns the full URL from the VITE_*_SERVICE_URL env var.
 *
 * To change a service port, update ONLY the .env file. Nothing else.
 */

export type ServiceKey =
  | 'auth'
  | 'iot'
  | 'biogas'
  | 'commerce'
  | 'reporting'
  | 'government'
  | 'gaushala'
  | 'purification'
  | 'manufacturing'
  | 'transport'
  | 'notification';

interface ServiceDefinition {
  contextPath: string;
  envVar: string;
}

/**
 * Service definitions: context path used for Vite proxy + env var name.
 * Context paths must match the proxy paths in vite.config.ts (auto-generated from .env).
 */
const SERVICE_DEFINITIONS: Record<ServiceKey, ServiceDefinition> = {
  auth:          { contextPath: '/auth-service',          envVar: 'VITE_AUTH_SERVICE_URL' },
  iot:           { contextPath: '/iot',                   envVar: 'VITE_IOT_SERVICE_URL' },
  biogas:        { contextPath: '/biogas-service',        envVar: 'VITE_BIOGAS_SERVICE_URL' },
  commerce:      { contextPath: '/commerce-service',      envVar: 'VITE_COMMERCE_SERVICE_URL' },
  reporting:     { contextPath: '/reporting-service',     envVar: 'VITE_REPORTING_SERVICE_URL' },
  government:    { contextPath: '/government-service',    envVar: 'VITE_GOVERNMENT_SERVICE_URL' },
  gaushala:      { contextPath: '/gaushala-service',      envVar: 'VITE_GAUSHALA_SERVICE_URL' },
  purification:  { contextPath: '/purification-service',  envVar: 'VITE_PURIFICATION_SERVICE_URL' },
  manufacturing: { contextPath: '/manufacturing-service', envVar: 'VITE_MANUFACTURING_SERVICE_URL' },
  transport:     { contextPath: '/transport-service',     envVar: 'VITE_TRANSPORT_SERVICE_URL' },
  notification:  { contextPath: '/notification-service',  envVar: 'VITE_NOTIFICATION_SERVICE_URL' },
};

const isDevelopment = import.meta.env.DEV;

/**
 * Get the base URL for a service.
 *
 * @param key - Service key (e.g., 'transport', 'auth')
 * @returns Base URL string
 *
 * @example
 * // Dev:  getServiceUrl('transport') → '/transport-service'
 * // Prod: getServiceUrl('transport') → 'https://saubhagya.dhanushhealthcare.com/transport-service'
 */
export function getServiceUrl(key: ServiceKey): string {
  const def = SERVICE_DEFINITIONS[key];
  if (!def) {
    throw new Error(`[serviceConfig] Unknown service key: "${key}". Valid keys: ${Object.keys(SERVICE_DEFINITIONS).join(', ')}`);
  }

  if (isDevelopment) {
    return def.contextPath;
  }

  return import.meta.env[def.envVar] || def.contextPath;
}

/**
 * Pre-computed service URL map for convenience.
 * Use this when you need all URLs at once, or for backward compatibility.
 *
 * @example
 * import { SERVICE_URLS } from '@/config/serviceConfig';
 * fetch(`${SERVICE_URLS.transport}/api/v1/routes`);
 */
export const SERVICE_URLS: Record<ServiceKey, string> = Object.fromEntries(
  (Object.keys(SERVICE_DEFINITIONS) as ServiceKey[]).map(key => [key, getServiceUrl(key)])
) as Record<ServiceKey, string>;

/**
 * Get all service definitions (for debugging/health checks).
 */
export function getServiceDefinitions(): Record<ServiceKey, ServiceDefinition> {
  return { ...SERVICE_DEFINITIONS };
}
